import {
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material"
import { RichTreeView } from "@mui/x-tree-view/RichTreeView"
import { ReactNode, SyntheticEvent, useEffect, useMemo, useState } from "react"
import { useGetProductsQuery } from "@store/services/productsService"
import {
  createFamilyNodes,
  createNodeTree,
  filterNodes,
  findNode,
  findNodeByKeys,
  getNodesIds,
  getUpdatedNodeTree,
} from "@components/ScenarioTree/helpers"
import { useDebounceValue, useLocalStorage } from "usehooks-ts"
import { ExpandLess, ExpandMore, KeyboardDoubleArrowLeft } from "@mui/icons-material"
import { OrganisationNode, ProductNode, ScenarioTreeNodeType } from "@components/ScenarioTree/types"
import NodeItem from "@components/ScenarioTree/NodeItem"
import { ProductFamily } from "@custom-types/products"
import Sidebar from "@components/Sidebar"

export type DefaultSelection = {
  organisationKey: string
  divisionKey: string
  productKey: string
  familyKey: string
  version: string
}
type ScenarioTreeProps = {
  onProductFamilySelect: (familyKey: string, causedByDefaultSelection: boolean) => void
  defaultSelection: DefaultSelection | undefined
  selectedFamilyKey: string
  productIdToRefetch?: string
  onProductRefetched?: () => void
  roleHint: string
  children?: ReactNode
}

const ScenarioTree = ({
  onProductFamilySelect,
  defaultSelection,
  selectedFamilyKey,
  productIdToRefetch,
  onProductRefetched,
  roleHint,
  children,
}: ScenarioTreeProps) => {
  const [isExpanded, setIsExpanded] = useState(true)
  const [expandedNodeIds, setExpandedNodeIds] = useState<string[]>([])
  const [filterValue, setFilterValue] = useDebounceValue("", 300)
  const [treeNodes, setTreeNodes] = useState<OrganisationNode[]>([])
  const [isDefaultSelectionApplied, setIsDefaultSelectionApplied] = useState(false)
  const [automaticCollapsing, setAutomaticCollapsing] = useLocalStorage(
    "scenarioSidebarAutomaticCollapsing",
    true,
  )
  const { data: products, isLoading } = useGetProductsQuery({ roleHint })

  useEffect(() => {
    const nodeTree = createNodeTree(products)
    setTreeNodes(nodeTree)
    setExpandedNodeIds(getNodesIds(...nodeTree))
  }, [products])

  const filteredGroupedItems = useMemo(
    () => filterNodes(treeNodes, filterValue),
    [treeNodes, filterValue],
  )
  const productNodeIdToPrefetch = defaultSelection
    ? `${defaultSelection.organisationKey}#${defaultSelection.divisionKey}#${defaultSelection.productKey}`
    : undefined

  const handleExpandedNodesChange = (_event: SyntheticEvent, nodeIds: string[]) => {
    setExpandedNodeIds(nodeIds)
  }

  const handleExpandCollapseAllToggle = () => {
    setExpandedNodeIds((oldExpanded) => (oldExpanded.length === 0 ? getNodesIds(...treeNodes) : []))
  }

  const handleFamiliesFetched = (
    nodeId: string,
    families: ProductFamily[],
    forceFetch: boolean,
  ) => {
    const node = findNode(treeNodes, nodeId)
    if (!node || node.type !== ScenarioTreeNodeType.PRODUCT) return

    const updatedNode: ProductNode = {
      ...node,
      hasFetchedFamilies: true,
      children: createFamilyNodes(
        families,
        node.organisationKey,
        node.divisionKey,
        node.productKey,
      ),
    }
    const updatedTree = getUpdatedNodeTree(
      treeNodes,
      updatedNode.id,
      updatedNode,
    ) as OrganisationNode[]
    setTreeNodes(updatedTree)
    setExpandedNodeIds([...expandedNodeIds, updatedNode.id])

    if (!defaultSelection || isDefaultSelectionApplied) return

    const familyNodetoSelect = findNodeByKeys(updatedTree, defaultSelection)
    if (!familyNodetoSelect) return

    handleNodeSelection(updatedTree, familyNodetoSelect.id, true, true)
    setIsDefaultSelectionApplied(true)
  }

  const handleNodeSelection = (
    nodeTree: OrganisationNode[],
    nodeId: string,
    isSelected: boolean,
    causedByDefaultSelection: boolean,
  ) => {
    if (!isSelected) return

    const node = findNode(nodeTree, nodeId)
    if (!node || node.type !== ScenarioTreeNodeType.FAMILY) return

    onProductFamilySelect(node.fullFamilyKey, causedByDefaultSelection)

    if (automaticCollapsing) setIsExpanded(false)
  }

  return (
    <Sidebar isExpanded={isExpanded} onExpand={() => setIsExpanded(true)}>
      <Grid container item direction="column" wrap="nowrap" gap={0.5}>
        <Grid item container justifyContent="space-around">
          <Grid item>
            <Tooltip title="Collapse sidebar">
              <IconButton onClick={() => setIsExpanded(!isExpanded)}>
                <KeyboardDoubleArrowLeft />
              </IconButton>
            </Tooltip>
          </Grid>
          <Grid item>
            <Tooltip title="Controls if sidebar should automatically collapse after selecting a graph">
              <FormControlLabel
                control={
                  <Switch
                    checked={automaticCollapsing}
                    onChange={(event) => setAutomaticCollapsing(event.target.checked)}
                  />
                }
                label="Automatic collapsing"
                labelPlacement="start"
                sx={{ ".MuiTypography-root": { fontSize: "13px" } }}
              />
            </Tooltip>
          </Grid>
          {children}
        </Grid>

        <Grid item>
          <Divider />
        </Grid>

        <Grid item>
          <Button
            disabled={isLoading}
            onClick={handleExpandCollapseAllToggle}
            startIcon={expandedNodeIds.length === 0 ? <ExpandMore /> : <ExpandLess />}
            fullWidth
          >
            {expandedNodeIds.length === 0 ? "Expand all" : "Collapse all"}
          </Button>
        </Grid>

        {isLoading && (
          <Grid item textAlign="center">
            <CircularProgress />
          </Grid>
        )}

        <Grid item>
          <TextField
            label="Filter"
            size="small"
            defaultValue=""
            onChange={(event) => setFilterValue(event.target.value)}
            fullWidth
            // hide it for now, most likely we need to switch it to API based search
            sx={{ display: "none" }}
          />
        </Grid>
      </Grid>

      <Grid
        item
        xs
        sx={{
          overflowY: "scroll",
          overflowX: "hidden",
        }}
      >
        <RichTreeView
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
          slots={{ item: NodeItem } as any}
          slotProps={{
            item: {
              onFamilyFetched: handleFamiliesFetched,
              prefetchProductItem: productNodeIdToPrefetch,
              selectedFamilyKey,
              // again pass these two down here
              productIdToRefetch,
              onProductRefetched,
              roleHint,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
            } as any, // how to correctly type custom props for slots?
          }}
          items={filteredGroupedItems}
          selectedItems={selectedFamilyKey}
          expandedItems={expandedNodeIds}
          onExpandedItemsChange={handleExpandedNodesChange}
          onItemSelectionToggle={(_event, nodeId, isSelected) =>
            handleNodeSelection(treeNodes, nodeId, isSelected, false)
          }
        />
      </Grid>
    </Sidebar>
  )
}

export default ScenarioTree
